import { Alert, Badge, Button, Field, Input } from '@personachat/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, MailCheck, MailPlus, RotateCcw, ShieldCheck, Trash2, UserCheck, UserX } from 'lucide-react';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { api } from '../../api/client';
import { lire, messageDe } from '../../api/call';
import type { components } from '../../api/schema';
import { useSession } from '../../auth/AuthProvider';
import { InfoList, Section, StatutBadge, TypeBadge } from '../../components/bits';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { formatDate, formatDateTime } from '../../lib/format';
import { EFFET_TYPE, LIBELLE_TYPE, TYPES_COMPTE, type TypeCompte } from '../../lib/labels';
import { PromotionDialog } from '../admins/PromotionEleve';

import { AccesSection, type Acces } from './AccesSection';

type Fiche = components['schemas']['UserDetail'] & { acces: Acces };

type Action = 'statut' | 'reinitialisation' | 'verifier' | 'renvoyer' | 'supprimer' | 'restaurer' | null;

/** Onglet Profil : identité, type, statut, e-mail, projets, et les actions sur le compte. */
export function ProfileTab({ eleve }: { eleve: Fiche }) {
  const { user: admin } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [action, setAction] = useState<Action>(null);
  const [changerType, setChangerType] = useState(false);
  const [promotion, setPromotion] = useState(false);

  const activite = useQuery({
    queryKey: ['eleve', eleve.id, 'activite'],
    queryFn: () => lire(api.GET('/api/admin/users/{id}/activity', { params: { path: { id: eleve.id } } })),
  });

  const rafraichir = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['eleve', eleve.id] }),
      queryClient.invalidateQueries({ queryKey: ['eleves'] }),
      queryClient.invalidateQueries({ queryKey: ['audit', eleve.id] }),
    ]);
  };

  const executer = async (appel: () => Promise<{ message?: string }>, succes: string) => {
    const r = await appel();
    await rafraichir();
    toast.succes(r.message ?? succes);
  };

  const path = { path: { id: eleve.id } };
  const supprime = !!eleve.deletedAt;
  const superAdmin = !!admin?.isSuperAdmin;

  return (
    <>
      <Section title="Identité">
        <InfoList
          items={[
            { label: 'Nom', value: [eleve.firstName, eleve.lastName].filter(Boolean).join(' ') || '—' },
            { label: 'E-mail', value: eleve.email },
            { label: 'Nom d’utilisateur', value: eleve.username ?? '—' },
            {
              label: 'Type de compte',
              value: (
                <span className="row">
                  <TypeBadge type={eleve.userType} isAdmin={eleve.isAdmin} />
                  {!eleve.isAdmin && !supprime && (
                    <button type="button" className="link-btn small" onClick={() => setChangerType(true)}>
                      Changer
                    </button>
                  )}
                </span>
              ),
            },
            { label: 'Statut', value: <StatutBadge isActive={eleve.isActive} deletedAt={eleve.deletedAt} /> },
            { label: 'Supprimé le', value: formatDateTime(eleve.deletedAt), hidden: !supprime },
            {
              label: 'E-mail vérifié',
              value: eleve.isEmailVerified ? 'Oui' : <Badge tone="danger">Non : l’élève n’a pas validé son inscription</Badge>,
            },
            { label: 'Inscription', value: formatDateTime(eleve.createdAt) },
            { label: 'Dernière connexion', value: formatDateTime(eleve.lastLoginAt) },
            {
              label: 'Clé d’inscription',
              value: eleve.accessKey ? (
                <span className="row">
                  {eleve.accessKeyId ? (
                    <Link className="link" to={`/cles-acces/${eleve.accessKeyId}`}>
                      <code>{eleve.accessKey.key}</code>
                    </Link>
                  ) : (
                    <code>{eleve.accessKey.key}</code>
                  )}
                  <Badge>{LIBELLE_TYPE[eleve.accessKey.keyType]}</Badge>
                  {eleve.accessKey.description && <span className="muted">{eleve.accessKey.description}</span>}
                </span>
              ) : (
                '—'
              ),
            },
          ]}
        />
      </Section>

      {!supprime && <AccesSection eleveId={eleve.id} userType={eleve.userType} isAdmin={eleve.isAdmin} acces={eleve.acces} onFait={rafraichir} />}

      <Section title="Actions sur le compte">
        {supprime ? (
          <div className="stack">
            <Alert tone="info">Compte supprimé : il n’apparaît plus dans les listes et ne peut plus se connecter. Son historique est conservé.</Alert>
            {superAdmin ? (
              <div>
                <Button icon={<RotateCcw size={15} aria-hidden="true" />} onClick={() => setAction('restaurer')}>
                  Restaurer le compte
                </Button>
              </div>
            ) : (
              <p className="note">Seul un super admin peut restaurer un compte.</p>
            )}
          </div>
        ) : (
          <div className="actions-list">
            {!eleve.isAdmin &&
              (eleve.isActive ? (
                <Button icon={<UserX size={15} aria-hidden="true" />} onClick={() => setAction('statut')}>
                  Désactiver
                </Button>
              ) : (
                <Button icon={<UserCheck size={15} aria-hidden="true" />} onClick={() => setAction('statut')}>
                  Réactiver
                </Button>
              ))}
            <Button icon={<KeyRound size={15} aria-hidden="true" />} onClick={() => setAction('reinitialisation')}>
              Envoyer un lien de réinitialisation
            </Button>
            {!eleve.isEmailVerified && (
              <>
                <Button icon={<MailPlus size={15} aria-hidden="true" />} onClick={() => setAction('renvoyer')}>
                  Renvoyer l’e-mail de vérification
                </Button>
                <Button icon={<MailCheck size={15} aria-hidden="true" />} onClick={() => setAction('verifier')}>
                  Marquer l’e-mail comme vérifié
                </Button>
              </>
            )}
            {superAdmin && !eleve.isAdmin && eleve.isActive && (
              <Button icon={<ShieldCheck size={15} aria-hidden="true" />} onClick={() => setPromotion(true)}>
                Passer en administrateur
              </Button>
            )}
            {superAdmin && admin?.id !== eleve.id && (
              <Button variant="danger" icon={<Trash2 size={15} aria-hidden="true" />} onClick={() => setAction('supprimer')}>
                Supprimer
              </Button>
            )}
          </div>
        )}
      </Section>

      <Section title="Projets">
        {activite.isError ? (
          <p className="note">Projets indisponibles : {messageDe(activite.error)}</p>
        ) : !activite.data ? (
          <p className="note">Chargement…</p>
        ) : activite.data.projects.length === 0 ? (
          <p className="note">Aucun projet créé dans l’application.</p>
        ) : (
          <ul className="plain-list">
            {activite.data.projects.map((p) => (
              <li key={p.id}>
                <strong>{p.name}</strong>
                {p.category && <span className="muted"> · {p.category}</span>}
                <span className="muted small">
                  {' '}
                  · avancement {p.progress} % · modifié le {formatDate(p.updatedAt)}
                  {p.archived && ' · archivé'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <ChangeTypeDialog open={changerType} onOpenChange={setChangerType} eleve={eleve} onFait={rafraichir} />
      <PromotionDialog
        open={promotion}
        onOpenChange={setPromotion}
        eleve={{ email: eleve.email, nom: [eleve.firstName, eleve.lastName].filter(Boolean).join(' ') || eleve.username || eleve.email }}
      />

      <ConfirmDialog
        open={action === 'statut'}
        onOpenChange={(o) => !o && setAction(null)}
        title={eleve.isActive ? 'Désactiver le compte' : 'Réactiver le compte'}
        confirmLabel={eleve.isActive ? 'Désactiver' : 'Réactiver'}
        tone={eleve.isActive ? 'danger' : 'primary'}
        onConfirm={() =>
          executer(() => lire(api.PATCH('/api/admin/users/{id}/toggle-status', { params: path })), eleve.isActive ? 'Compte désactivé.' : 'Compte réactivé.')
        }
      >
        {eleve.isActive ? (
          <p>
            <strong>{eleve.email}</strong> ne pourra plus se connecter et ses sessions ouvertes s’arrêtent. Ses données et ses crédits sont
            conservés. Son accès est en pause : le temps ne compte pas, sa date de fin sera repoussée d’autant à la réactivation.
          </p>
        ) : (
          <p>
            <strong>{eleve.email}</strong> pourra de nouveau se connecter.
            {eleve.acces.pausedAt && ' Sa date de fin d’accès est repoussée du temps de pause.'}
          </p>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={action === 'reinitialisation'}
        onOpenChange={(o) => !o && setAction(null)}
        title="Envoyer un lien de réinitialisation"
        confirmLabel="Envoyer"
        onConfirm={() => executer(() => lire(api.PUT('/api/admin/users/{id}/password', { params: path })), 'Lien envoyé.')}
      >
        <p>
          Un e-mail avec un lien pour choisir un nouveau mot de passe part à <strong>{eleve.email}</strong>. Le mot de passe actuel reste valable
          tant que le lien n’est pas utilisé.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={action === 'renvoyer'}
        onOpenChange={(o) => !o && setAction(null)}
        title="Renvoyer l’e-mail de vérification"
        confirmLabel="Renvoyer"
        onConfirm={() => executer(() => lire(api.POST('/api/admin/users/{id}/resend-verification', { params: path })), 'E-mail envoyé.')}
      >
        <p>
          Un nouveau lien de vérification, valable 24 h, part à <strong>{eleve.email}</strong>.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={action === 'verifier'}
        onOpenChange={(o) => !o && setAction(null)}
        title="Marquer l’e-mail comme vérifié"
        confirmLabel="Marquer vérifié"
        onConfirm={() => executer(() => lire(api.POST('/api/admin/users/{id}/verify-email', { params: path })), 'E-mail marqué comme vérifié.')}
      >
        <p>
          À utiliser quand l’élève ne reçoit pas l’e-mail de vérification et que son adresse <strong>{eleve.email}</strong> a été confirmée par
          un autre moyen.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={action === 'supprimer'}
        onOpenChange={(o) => !o && setAction(null)}
        title="Supprimer le compte"
        confirmLabel="Supprimer"
        tone="danger"
        retaper={eleve.email}
        onConfirm={async () => {
          await lire(api.DELETE('/api/admin/users/{id}', { params: path }));
          await rafraichir();
          toast.succes('Compte supprimé.');
          navigate('/eleves');
        }}
      >
        <p>
          Le compte disparaît des listes et ne peut plus se connecter. Son historique (crédits, paiements, conversations) est conservé et un
          super admin peut le restaurer.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={action === 'restaurer'}
        onOpenChange={(o) => !o && setAction(null)}
        title="Restaurer le compte"
        confirmLabel="Restaurer"
        onConfirm={() => executer(() => lire(api.POST('/api/admin/users/{id}/restore', { params: path })), 'Compte restauré.')}
      >
        <p>
          <strong>{eleve.email}</strong> réapparaît dans les listes et peut de nouveau se connecter.
        </p>
      </ConfirmDialog>
    </>
  );
}

/** Changer le type de compte (motif obligatoire), avec l'effet de chaque type en clair. */
function ChangeTypeDialog({
  open,
  onOpenChange,
  eleve,
  onFait,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  eleve: Fiche;
  onFait: () => Promise<void>;
}) {
  const id = useId();
  const toast = useToast();
  const [type, setType] = useState<TypeCompte>('NORMAL');
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (open) {
      setType(eleve.userType === 'NORMAL' ? 'GUEST' : 'NORMAL');
      setMotif('');
      setErreur(null);
    }
  }, [open, eleve.userType]);

  const valider = async (e: FormEvent) => {
    e.preventDefault();
    setEnvoi(true);
    setErreur(null);
    try {
      const r = await lire(api.PATCH('/api/admin/users/{id}/type', { params: { path: { id: eleve.id } }, body: { type, motif: motif.trim() } }));
      await onFait();
      const effets = [
        r.effets.retires > 0 ? `${r.effets.retires} coach(s) d’événement retiré(s)` : null,
        r.effets.debloques.length > 0 ? `débloqué(s) : ${r.effets.debloques.join(', ')}` : null,
      ].filter(Boolean);
      toast.succes(`Compte passé en ${LIBELLE_TYPE[type]}${effets.length ? ` — ${effets.join(' ; ')}` : ''}.`);
      onOpenChange(false);
    } catch (err) {
      setErreur(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Modal open={open} onOpenChange={(o) => !envoi && onOpenChange(o)} title="Changer le type de compte" description={`Actuellement : ${LIBELLE_TYPE[eleve.userType]}.`}>
      <form className="stack" onSubmit={valider}>
        <fieldset className="choix">
          <legend className="ui-field__label">Nouveau type</legend>
          {TYPES_COMPTE.filter((t) => t !== eleve.userType).map((t) => (
            <label key={t} className={t === type ? 'choix__option choix__option--active' : 'choix__option'}>
              <input type="radio" name={`${id}-type`} value={t} checked={t === type} onChange={() => setType(t)} />
              <span>
                <strong>{LIBELLE_TYPE[t]}</strong>
                <span className="note">{EFFET_TYPE[t]}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <p className="note">
          {type === 'NORMAL'
            ? 'Les crédits sont complétés jusqu’à ceux d’une inscription Normal, et la première année d’accès commence aujourd’hui si elle n’avait pas déjà commencé.'
            : 'Le solde de crédits ne change pas.'}
        </p>
        <Field id={`${id}-motif`} label="Motif" hint="Obligatoire, inscrit au journal d’audit.">
          <Input id={`${id}-motif`} value={motif} onChange={(e) => setMotif(e.target.value)} maxLength={300} placeholder="Ex. : paiement de la formation reçu le 29/09" />
        </Field>
        {erreur && <Alert>{erreur}</Alert>}
        <div className="modal__footer modal__footer--inline">
          <Button onClick={() => onOpenChange(false)} disabled={envoi}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" loading={envoi} disabled={motif.trim().length < 3}>
            Passer en {LIBELLE_TYPE[type]}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
